/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { SxProps, Theme } from '@mui/material';
import { InputAdornment } from '@mui/material';
import type {
  DateTimePickerProps,
  DateTimePickerSlotProps,
} from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { dateUtils } from '@opengeoweb/shared';
import { CalendarToday } from '@opengeoweb/theme';
import { getErrors } from './formUtils';
import ReactHookFormFormControl from './ReactHookFormFormControl';
import type { ReactHookFormInput } from './types';
import { defaultProps } from './utils';

const OpenPicker = (): React.ReactElement => <CalendarToday />;

const getTimezoneOffset = (value: Date): number =>
  value.getTimezoneOffset() * 60000;

const makeLocalAppearUTC = (value: Date): Date =>
  new Date(value.getTime() + getTimezoneOffset(value));

const localToUTC = (dateTime: Date): Date =>
  new Date(dateTime.getTime() - getTimezoneOffset(dateTime));

/**
 * Should return an isostring in the form "YYYY-MM-DDThh:mmZ";
 * The milli seconds section should not be included, strictly YYYY-MM-DDThh:mm:ssZ is important
 * @param value input Date
 * @returns iso string in YYYY-MM-DDThh:mm format.
 */
export const getFormattedValueForDatePicker = (value: Date): string | null => {
  return (
    dateUtils.dateToString(value, "yyyy-MM-dd'T'HH:mm:00'Z'", true) ?? null
  );
};

export const getDateValue = (value: string): Date | null => {
  if (value) {
    const dateValue =
      dateUtils.stringToDate(value, 'yyyy/MM/dd HH:mm', false) ??
      dateUtils.isoStringToDate(value, false) ??
      null;
    if (dateValue) {
      return makeLocalAppearUTC(dateUtils.setSeconds(dateValue, 0));
    }
    return null;
  }
  return null;
};

type ReactHookKeyboardDateTimePickerProps = DateTimePickerProps &
  DateTimePickerSlotProps &
  ReactHookFormInput<{
    defaultNullValue?: string | null;
    value?: string;
    onChange?: (value: Date | string | null) => void;
    format?: string;
    sx?: SxProps<Theme>;
  }> & { 'data-testid'?: string; shouldHideUTC?: boolean };

const ReactHookKeyboardDateTimePicker: React.FC<
  ReactHookKeyboardDateTimePickerProps
> = ({
  name,
  rules,
  disabled,
  label = 'Select date and time',
  format = dateUtils.DATE_FORMAT_DATEPICKER,
  openTo = 'hours',
  defaultNullValue = null,
  helperText = '',
  onChange = (): void => {},
  className,
  sx,
  isReadOnly,
  shouldHideUTC,
  disablePast,
  disableFuture,
  ...otherProps
}: ReactHookKeyboardDateTimePickerProps) => {
  const { control } = useFormContext();
  const {
    field: { onChange: onChangeField, value, ref },
    formState: { errors: formErrors },
  } = useController({
    name,
    control,
    rules,
    defaultValue: defaultNullValue,
    ...defaultProps,
  });

  const errors = getErrors(name, formErrors);

  // Ensure value is a Date object

  const dateValue = getDateValue(value);
  const now = makeLocalAppearUTC(dateUtils.utc());
  const minDateTime = disablePast ? now : null;
  const maxDateTime = disableFuture ? now : null;

  return (
    <ReactHookFormFormControl
      className={className}
      disabled={disabled}
      errors={errors}
      isReadOnly={isReadOnly}
      sx={sx}
    >
      <DateTimePicker
        ampm={false}
        desktopModeMediaQuery="@media (min-width: 720px)"
        disabled={disabled}
        format={format}
        inputRef={ref}
        label={label}
        maxDateTime={maxDateTime!}
        minDateTime={minDateTime!}
        onChange={(value: Date | null): void => {
          if (!value) {
            onChange(null);
            onChangeField(null);
            return;
          }
          const formattedDate = getFormattedValueForDatePicker(
            localToUTC(value),
          );
          onChangeField(formattedDate);
          onChange(formattedDate);
        }}
        openTo={openTo}
        slotProps={{
          textField: {
            variant: 'filled',
            helperText,
            error: !!errors,
            name,
            slotProps: {
              htmlInput: {
                placeholder: disabled ? '' : format.toLowerCase(),
              },
              input: {
                ...(!shouldHideUTC && {
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      style={{ paddingTop: '16px' }}
                    >
                      UTC
                    </InputAdornment>
                  ),
                }),
              },
            },

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            'data-testid': otherProps['data-testid'],
            ...(isReadOnly && {
              readOnly: true,
            }),
          },
          field: { openPickerButtonPosition: 'start' },
          openPickerButton: {
            size: 'small',
            sx: { padding: 0 },
          },
        }}
        slots={{
          openPickerIcon: OpenPicker,
        }}
        timeSteps={{ minutes: 1 }}
        value={dateValue}
        {...otherProps}
      />
    </ReactHookFormFormControl>
  );
};
export default ReactHookKeyboardDateTimePicker;
